<template>
  <div class="seo-page steam-deck-sale">
    <section class="seo-hero">
      <span class="seo-badge">Steam Autumn Sale 2025</span>
      <h1>Best Steam Deck Games on Sale Right Now</h1>
      <p>
        The Steam Autumn Sale 2025 kicked off yesterday, and the Deck-friendly library is stacked with deep discounts.
        We gathered the most exciting portable picks so you can lock in savings before the prices bounce back.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">Launch steamplayti.me</router-link>
        <a class="seo-secondary-link" href="#deck-sale-list">Browse every discount →</a>
      </div>
      <ul class="seo-highlight-list">
        <li>
          <span class="seo-highlight-icon">🔥</span>
          <div>Fresh savings from story epics to bite-sized roguelikes – all proven to be a joy on Steam Deck.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">🎯</span>
          <div>Sorted with handheld play in mind: short boot times, offline modes, and controller-first support.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">🚀</span>
          <div>Track every session with steamplayti.me and see your Deck backlog evolve day by day.</div>
        </li>
      </ul>
    </section>

    <section class="seo-section">
      <h2>Featured Steam Deck Deals</h2>
      <p>
        Start with these standout Deck-ready games at steep discounts. Each pick is a single-player or co-op essential
        that already shines on Valve's handheld without configuration tinkering.
      </p>
      <div class="featured-grid">
        <article v-for="game in featuredGames" :key="game.appId" class="sale-card">
          <a :href="game.storeUrl" target="_blank" rel="noopener" class="sale-card__image">
            <img :src="game.imageUrl" :alt="`${game.name} Steam banner`" loading="lazy" />
          </a>
          <div class="sale-card__body">
            <h3>{{ game.name }}</h3>
            <p class="sale-card__blurb">{{ game.pitch }}</p>
            <div class="sale-card__meta">
              <span class="sale-card__discount">-{{ game.discount }}%</span>
              <span class="sale-card__price">{{ game.price }}</span>
            </div>
            <a :href="game.storeUrl" target="_blank" rel="noopener" class="sale-card__cta">View on Steam</a>
          </div>
        </article>
      </div>
    </section>

    <section class="seo-section">
      <h2>Why These Picks Shine on Steam Deck</h2>
      <p>
        From verified RPG blockbusters to indie survival loops, these games balance performance, quick suspend-and-resume,
        and offline friendliness. Pair them with our tracker to watch playtime spikes the moment you dive in.
      </p>
      <div class="seo-grid">
        <div class="seo-card" v-for="highlight in highlights" :key="highlight.title">
          <h3>{{ highlight.title }}</h3>
          <p>{{ highlight.copy }}</p>
        </div>
      </div>
    </section>

    <section class="seo-section" id="deck-sale-list">
      <h2>Complete Discount List for Steam Deck</h2>
      <p>
        Every title below is discounted during the Steam Autumn Sale 2025 and plays great on Deck. Prices are in USD and
        reflect the sale sticker at the time this guide went live.
      </p>
      <div class="sale-table">
        <header class="sale-table__header">
          <span>Game</span>
          <span>Discount</span>
          <span>Sale Price</span>
          <span></span>
        </header>
        <div class="sale-table__row" v-for="game in saleGames" :key="game.appId">
          <div class="sale-table__info">
            <a :href="game.storeUrl" target="_blank" rel="noopener">
              <img :src="game.imageUrl" :alt="`${game.name} Steam header art`" loading="lazy" />
            </a>
            <div>
              <h3>{{ game.name }}</h3>
              <p>{{ game.deckPitch }}</p>
            </div>
          </div>
          <div class="sale-table__meta">
            <span class="sale-table__discount">-{{ game.discount }}%</span>
            <span class="sale-table__price">{{ game.price }}</span>
          </div>
          <a :href="game.storeUrl" target="_blank" rel="noopener" class="sale-table__link">Open Store</a>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Track Your Steam Deck Sale Wins</h2>
      <p>
        steamplayti.me syncs your playtime the moment your new favorites download. Add each sale pickup to your profile
        and spot which discounted titles turn into daily drivers with up-to-the-minute playtime graphs.
      </p>
      <div class="cta-banner">
        <h3>Deck backlog exploding? Let steamplayti.me keep score.</h3>
        <router-link to="/" class="cta-banner__button">Start tracking now</router-link>
      </div>
    </section>
    <SeoRelatedTools heading="Track Your Own Steam Playtime" />
  </div>
</template>

<script>
import { applyMeta, articleStructuredData } from '../utils/meta';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';

const SALE_GAMES = [
  { name: "Baldur's Gate 3", appId: 1086940, discount: 25, price: '$26.24' },
  { name: 'Vampire Survivors', appId: 1794680, discount: 25, price: '$2.61' },
  { name: 'Dave the Diver', appId: 1868140, discount: 40, price: '$7.79' },
  { name: 'Hogwarts Legacy', appId: 990080, discount: 80, price: '$11.99' },
  { name: 'Palworld', appId: 1623730, discount: 25, price: '$11.24' },
  { name: 'Cyberpunk 2077', appId: 1091500, discount: 65, price: '$15.74' },
  { name: 'Stardew Valley', appId: 413150, discount: 30, price: '$4.19' },
  { name: 'Red Dead Redemption 2', appId: 1174180, discount: 75, price: '$14.99' },
  { name: 'Hades', appId: 1145360, discount: 75, price: '$2.06' },
  { name: 'Brotato', appId: 1942280, discount: 40, price: '$1.79' },
  { name: 'The Elder Scrolls V: Skyrim Special Edition', appId: 489830, discount: 75, price: '$5.99' },
  { name: 'The Witcher 3: Wild Hunt', appId: 292030, discount: 80, price: '$5.99' },
  { name: 'Slay the Spire', appId: 646570, discount: 75, price: '$2.87' },
  { name: 'Cult of the Lamb', appId: 1313140, discount: 50, price: '$6.24' },
  { name: 'Dead Cells', appId: 588650, discount: 50, price: '$6.24' },
  { name: 'Monster Hunter: World', appId: 582010, discount: 67, price: '$7.42' },
  { name: 'Persona 5 Royal', appId: 1687950, discount: 65, price: '$20.99' },
  { name: "Marvel's Spider-Man Remastered", appId: 1817070, discount: 50, price: '$29.99' },
  { name: 'Dredge', appId: 1562430, discount: 50, price: '$6.24' },
  { name: 'Fallout 4', appId: 377160, discount: 60, price: '$4.79' },
  { name: "No Man's Sky", appId: 275850, discount: 60, price: '$10.79' },
  { name: 'Monster Hunter Rise', appId: 1446780, discount: 75, price: '$7.49' },
  { name: 'Balatro', appId: 2379780, discount: 10, price: '$7.19' },
  { name: 'Diablo IV', appId: 2344520, discount: 55, price: '$22.49' },
  { name: 'Halls of Torment', appId: 2218750, discount: 25, price: '$3.74' },
  { name: 'Halo: The Master Chief Collection', appId: 976730, discount: 75, price: '$9.99' },
  { name: 'Helldivers 2', appId: 553850, discount: 20, price: '$31.99' },
  { name: 'Grand Theft Auto IV: The Complete Edition', appId: 12210, discount: 70, price: '$5.99' },
  { name: 'God of War', appId: 1593500, discount: 60, price: '$19.99' },
  { name: "Sid Meier's Civilization VI", appId: 289070, discount: 90, price: '$5.99' },
  { name: 'Fallout: New Vegas', appId: 22380, discount: 50, price: '$2.99' },
  { name: 'Deep Rock Galactic: Survivor', appId: 2321470, discount: 30, price: '$5.10' },
  { name: 'Street Fighter 6', appId: 1364780, discount: 50, price: '$14.99' },
  { name: 'Octopath Traveler II', appId: 1971650, discount: 60, price: '$23.99' },
  { name: 'Terraria', appId: 105600, discount: 50, price: '$2.89' },
  { name: 'Valheim', appId: 892970, discount: 50, price: '$6.59' },
  { name: 'Euro Truck Simulator 2', appId: 227300, discount: 75, price: '$2.52' },
  { name: 'Starfield', appId: 1716740, discount: 40, price: '$25.19' },
  { name: 'Sekiro: Shadows Die Twice - GOTY Edition', appId: 814380, discount: 50, price: '$29.99' },
  { name: 'Project Zomboid', appId: 108600, discount: 33, price: '$7.02' },
  { name: 'Persona 3 Reload', appId: 2161700, discount: 60, price: '$27.99' },
  { name: 'STAR WARS Jedi: Fallen Order', appId: 1172380, discount: 75, price: '$9.99' },
  { name: 'Sea of Stars', appId: 1244090, discount: 35, price: '$11.69' },
  { name: 'Lethal Company', appId: 1966720, discount: 25, price: '$4.34' },
  { name: 'DARK SOULS III', appId: 374320, discount: 50, price: '$19.99' },
  { name: 'Final Fantasy VII Remake Intergrade', appId: 1462040, discount: 40, price: '$19.19' },
  { name: 'Divinity: Original Sin 2 - Definitive Edition', appId: 435150, discount: 70, price: '$7.79' },
  { name: 'Disco Elysium - The Final Cut', appId: 632470, discount: 75, price: '$4.74' },
  { name: 'Days Gone', appId: 1259420, discount: 50, price: '$19.99' },
  { name: "Marvel's Spider-Man: Miles Morales", appId: 1817190, discount: 50, price: '$24.99' },
  { name: 'Yakuza: Like a Dragon', appId: 1235140, discount: 50, price: '$9.99' },
  { name: 'Coral Island', appId: 1158160, discount: 33, price: '$10.04' },
  { name: 'Disney Dreamlight Valley', appId: 1401590, discount: 30, price: '$20.30' },
  { name: 'Armored Core VI Fires of Rubicon', appId: 1888160, discount: 40, price: '$23.99' },
  { name: 'Resident Evil 2', appId: 883710, discount: 75, price: '$7.49' },
  { name: 'Mass Effect Legendary Edition', appId: 1328670, discount: 90, price: '$5.99' },
  { name: 'Risk of Rain 2', appId: 632360, discount: 67, price: '$4.12' },
  { name: 'Yakuza 0', appId: 638970, discount: 45, price: '$10.99' },
  { name: "Batman: Arkham Knight", appId: 208650, discount: 80, price: '$3.99' },
  { name: 'DARK SOULS: REMASTERED', appId: 570940, discount: 50, price: '$12.49' },
  { name: 'Persona 4 Golden', appId: 1113000, discount: 50, price: '$9.99' },
  { name: 'RimWorld', appId: 294100, discount: 20, price: '$14.39' },
  { name: 'PowerWash Simulator', appId: 1290000, discount: 50, price: '$9.99' },
  { name: 'Left 4 Dead 2', appId: 550, discount: 80, price: '$1.15' },
  { name: "Death Stranding Director's Cut", appId: 1850570, discount: 50, price: '$9.49' },
  { name: 'LEGO Star Wars: The Skywalker Saga', appId: 920210, discount: 80, price: '$9.99' },
  { name: 'Dead by Daylight', appId: 381210, discount: 60, price: '$5.99' },
  { name: 'Deep Rock Galactic', appId: 548430, discount: 67, price: '$4.94' },
  { name: 'Uncharted: Legacy of Thieves Collection', appId: 1659420, discount: 60, price: '$19.99' },
  { name: 'Like a Dragon: Infinite Wealth', appId: 2072450, discount: 60, price: '$27.99' },
  { name: 'Granblue Fantasy: Relink', appId: 881020, discount: 55, price: '$17.99' },
  { name: 'Middle-earth: Shadow of War', appId: 356190, discount: 90, price: '$4.99' },
  { name: 'House Flipper', appId: 613100, discount: 80, price: '$2.99' },
];

const META = {
  title: 'Steam Deck Sale 2025: Best Discounted Games | steamplayti.me',
  description: 'Browse every discounted Steam Deck standout during the Steam Autumn Sale 2025. RPG epics, indie hits, and co-op staples – all with quick links and savings.',
  keywords: 'Steam Deck sale, Steam Autumn Sale 2025, Steam Deck best games, Steam Deck discounts, Steam deals, portable gaming sale',
  canonical: 'https://steamplayti.me/blog/steam-deck-sale/',
  ogTitle: 'Steam Deck Autumn Sale Picks',
  ogDescription: 'See the strongest Steam Deck discounts from the Autumn Sale 2025 and jump straight into handheld-ready adventures.',
  ogUrl: 'https://steamplayti.me/blog/steam-deck-sale/',
  twitterTitle: 'Steam Deck Autumn Sale Picks',
  twitterDescription: 'Grab the best Steam Deck discounts from the Steam Autumn Sale 2025 and track them with steamplayti.me.',
  structuredData: articleStructuredData({
    headline: 'Best Steam Deck Games on Sale Right Now',
    description: 'Browse every discounted Steam Deck standout during the Steam Autumn Sale 2025. RPG epics, indie hits, and co-op staples – all with quick links and savings.',
    canonical: 'https://steamplayti.me/blog/steam-deck-sale/',
    datePublished: '2025-09-30',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: '/blog/' },
      { name: 'Steam Deck Sale 2025: Best Discounted Games', path: 'https://steamplayti.me/blog/steam-deck-sale/' },
    ],
  }),
};

const DECK_PITCHES = {
  "Baldur's Gate 3": 'Party banter and tactical dice rolls feel great on the go.',
  'Vampire Survivors': 'Minimal controls unleash sprawling bullet-heaven runs on handheld.',
  'Dave the Diver': 'Morning dives and sushi nights are perfect bite-sized Deck sessions.',
  'Hogwarts Legacy': 'Tour Hogwarts with gyro aiming and smooth quality mode presets.',
  'Palworld': 'Catch pals and raid bases with stable 40Hz handheld performance.',
  'Cyberpunk 2077': 'Night City quests stream well using FSR and tuned low settings.',
  'Stardew Valley': 'Simple controls and quick days make farm life ideal on Deck.',
  'Red Dead Redemption 2': "Cinematic rides stay steady with Rockstar's 40 FPS preset.",
  'Hades': "Fast roguelike dashes respond instantly with Deck's twin sticks.",
  'Brotato': 'Five-minute arena runs pair perfectly with suspend and resume.',
  'The Elder Scrolls V: Skyrim Special Edition': 'Modest presets keep dragon hunts smooth even docked at 40Hz.',
  'The Witcher 3: Wild Hunt': 'Story-heavy quests thrive with quick-resume and controller-first UI.',
  'Slay the Spire': 'Card drafting marathons sip battery and feel great with buttons.',
  'Cult of the Lamb': 'Village management and crusades translate cleanly to handheld.',
  'Dead Cells': 'Precision dodges and rolls shine with responsive Deck inputs.',
  'Monster Hunter: World': 'Hunts stay crisp with a 40 FPS lock and gyro aiming.',
  'Persona 5 Royal': 'Turn-based school days flow effortlessly on the OLED handheld.',
  "Marvel's Spider-Man Remastered": 'Swing through Manhattan with smooth 40 FPS performance mode.',
  'Dredge': 'Late-night fishing runs and mysteries feel natural on portable.',
  'Fallout 4': 'Settlement building and VATS action stay stable on Deck hardware.',
  "No Man's Sky": 'Procedural exploration feels great with gyro flight controls.',
  'Monster Hunter Rise': 'Silkbind moves land reliably thanks to tight controller support.',
  'Balatro': 'Quick deck-building hands are built for handheld bursts.',
  'Diablo IV': 'Seasonal grinding and couch co-op play nicely with Deck controls.',
  'Halls of Torment': 'Retro horde chaos is perfect for short handheld bursts.',
  'Halo: The Master Chief Collection': 'Classic campaigns and co-op playlists hit 60 FPS in handheld.',
  'Helldivers 2': 'Drop pods and bug hunts run steady with community settings.',
  'Grand Theft Auto IV: The Complete Edition': 'Liberty City sandbox streams well with tuned FSR presets.',
  'God of War': 'Leviathan Axe combat remains fluid at 40 FPS quality mode.',
  "Sid Meier's Civilization VI": 'One-more-turn marathons pair perfectly with touchpad cursor support.',
  'Fallout: New Vegas': 'Wasteland runs stay smooth using community Deck tweaks.',
  'Deep Rock Galactic: Survivor': 'Twin-stick mining loops fit easily into portable sessions.',
  'Street Fighter 6': 'Modern controls deliver crisp online fights on Deck.',
  'Octopath Traveler II': "HD-2D journeys glow on the Deck's display and controls.",
  'Terraria': 'Pixel digging and crafting thrive with touchpad cursor aiming.',
  'Valheim': 'Co-op building adventures perform well with community Vulkan config.',
  'Euro Truck Simulator 2': 'Long hauls cruise smoothly with gyro steering and haptics.',
  'Starfield': 'Constellation missions stream fine with tuned low presets.',
  'Sekiro: Shadows Die Twice - GOTY Edition': 'Parry-perfect combat stays responsive at 40 FPS.',
  'Project Zomboid': 'Isometric survival micromanagement works great with trackpads.',
  'Persona 3 Reload': 'Dorm life and dungeon dives work perfectly with suspend play.',
  'STAR WARS Jedi: Fallen Order': 'Lightsaber duels feel slick with cinematic performance preset.',
  'Sea of Stars': 'Retro turn-based battles pop on the OLED handheld.',
  'Lethal Company': 'Scavenger runs and voice chat excel in portable co-op.',
  'DARK SOULS III': 'Lothric showdowns hit steady frame pacing at 40Hz mode.',
  'Final Fantasy VII Remake Intergrade': 'Action RPG spectacle hums at optimized 40 FPS preset.',
  'Divinity: Original Sin 2 - Definitive Edition': 'Co-op campaign menus feel native to Deck controls.',
  'Disco Elysium - The Final Cut': 'Narrative investigations and voiced dialogue suit comfy handheld play.',
  'Days Gone': 'Open-world bike runs stay stable with FSR quality mode.',
  "Marvel's Spider-Man: Miles Morales": 'Harlem swings sparkle at 40 FPS with ray tracing off.',
  'Yakuza: Like a Dragon': 'Turn-based street brawls and minigames run smooth portable.',
  'Coral Island': 'Cozy farming and diving loops are relaxing with thumbsticks.',
  'Disney Dreamlight Valley': 'Life-sim quests translate nicely to handheld controls.',
  'Armored Core VI Fires of Rubicon': 'Mech tuning and sorties thrive with gyro aim support.',
  'Resident Evil 2': 'Terrifying corridors stay sharp on Deck performance mode.',
  'Mass Effect Legendary Edition': 'Legendary trilogy holds 40 FPS with controller-first UI.',
  'Risk of Rain 2': 'Procedural boss runs feel great with gyro-assisted aim.',
  'Yakuza 0': 'Dual-protagonist drama and minigames shine handheld at 60 FPS.',
  'Batman: Arkham Knight': 'Batarang combos work well with FSR and a 40 FPS cap.',
  'DARK SOULS: REMASTERED': 'Classic Lordran runs great with 60 FPS handheld play.',
  'Persona 4 Golden': 'Golden social sim loops are perfect for portable grinding.',
  'RimWorld': 'Colony management handles nicely with touchpad cursor setup.',
  'PowerWash Simulator': 'Relaxing cleanup jobs map well to gyro aiming.',
  'Left 4 Dead 2': 'Zombie rushes run flawlessly with four-player local play.',
  "Death Stranding Director's Cut": 'Courier treks feel cinematic with tuned medium preset.',
  'LEGO Star Wars: The Skywalker Saga': 'Co-op brick smashing shines with drop-in handheld play.',
  'Dead by Daylight': 'Asymmetrical matches stay steady with cross-play enabled.',
  'Deep Rock Galactic': 'Mining missions and beer runs stay smooth in handheld.',
  'Uncharted: Legacy of Thieves Collection': 'Nate and Chloe adventures maintain 40 FPS handheld.',
  'Like a Dragon: Infinite Wealth': 'Hawaii turn-based chaos feels great with Deck controls.',
  'Granblue Fantasy: Relink': 'Action RPG raid parties pop with smooth performance.',
  'Middle-earth: Shadow of War': 'Nemesis sieges perform well with optimized presets.',
  'House Flipper': 'Renovation jobs click with gyro painting and suspend support.',
};

function buildGameData(game) {
  const storeUrl = `https://store.steampowered.com/app/${game.appId}`;
  const imageUrl = `https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/${game.appId}/header.jpg`;
  return {
    ...game,
    storeUrl,
    imageUrl,
    deckPitch: game.deckPitch || DECK_PITCHES[game.name] || defaultDeckPitch(game.name),
  };
}

function defaultDeckPitch(name) {
  if (/like a dragon/i.test(name)) {
    return 'Turn-based brawling that lights up the Deck screen with wild summons and karaoke detours.';
  }
  if (/yakuza 0/i.test(name)) {
    return 'Vintage Kamurocho chaos that runs flawlessly at 60 FPS on Deck.';
  }
  if (/mass effect/i.test(name)) {
    return 'Legendary trilogy runs smoothly with controller-first UI for long trips.';
  }
  if (/baldur/i.test(name)) {
    return 'CRPG greatness with verified Deck support and cooperative couch play.';
  }
  if (/cyberpunk/i.test(name)) {
    return 'Night City handheld thanks to the latest optimizations and FSR tweaks.';
  }
  if (/helldivers/i.test(name)) {
    return 'Drop in with friends for portable co-op bug hunting at a steady frame rate.';
  }
  if (/resident evil 2/i.test(name)) {
    return 'Remake terror that stays crisp on Deck with cinematic controls.';
  }
  if (/terraria/i.test(name)) {
    return 'Pixellated sandbox adventure perfect for quick suspend-and-resume bursts.';
  }
  if (/palworld/i.test(name)) {
    return 'Creature collecting and base building that already plays great on Deck.';
  }
  if (/starfield/i.test(name)) {
    return 'Take Bethesda’s space opera on the go with tuned low-spec presets.';
  }
  if (/risk of rain 2/i.test(name)) {
    return 'Three-minute runs or hour-long slogs; either way the loop shines in handheld mode.';
  }
  if (/house flipper/i.test(name)) {
    return 'Cozy renovation sessions that thrive with touchpad cursor support.';
  }
  return `${name} stays smooth and responsive on Deck hardware.`;
}

const featuredPitches = {
  "Baldur's Gate 3": 'The definitive CRPG now 25% off, with cloud saves that jump between Deck and desktop.',
  'Cyberpunk 2077': 'Night City is finally portable thanks to FSR 3 updates and a massive 65% discount.',
  'Palworld': 'Grab the co-op monster early with 25% off and co-op that feels great on Deck controls.',
  'Dave the Diver': 'Perfect bite-sized Deck sessions for deep-sea runs, now almost half off.',
  'Red Dead Redemption 2': '75% off the open-world masterpiece that hums along at 40 FPS tweaked presets.',
  'Helldivers 2': 'Suit up for 20% off; gyro aiming and couch co-op keep the galactic war mobile.',
};

export default {
  name: 'SteamDeckSalePage',
  components: { SeoRelatedTools },
  data() {
    const saleGames = SALE_GAMES.map(buildGameData).sort((a, b) => {
      if (b.discount === a.discount) {
        return a.name.localeCompare(b.name);
      }
      return b.discount - a.discount;
    });

    const featuredNames = [
      "Baldur's Gate 3",
      'Cyberpunk 2077',
      'Palworld',
      'Dave the Diver',
      'Red Dead Redemption 2',
      'Helldivers 2',
    ];

    const featuredGames = featuredNames
      .map((name) => saleGames.find((game) => game.name === name))
      .filter(Boolean)
      .map((game) => ({
        ...game,
        pitch: featuredPitches[game.name] || 'Deck-ready and discounted for the Autumn Sale.',
      }));

    return {
      saleGames,
      featuredGames,
      highlights: [
        {
          title: 'Verified & Portable',
          copy: 'These picks deliver smooth performance on Deck hardware, whether you prefer 40 Hz battery saver modes or docked 60 FPS play.',
        },
        {
          title: 'Controller-Native Experiences',
          copy: 'No keyboard gymnastics required – every title here feels right at home with the Deck layout and haptics.',
        },
        {
          title: 'Daily Playtime Signals',
          copy: 'Use steamplayti.me to log each sale pickup and monitor daily playtime swings as new games enter the rotation.',
        },
      ],
    };
  },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};

</script>

<style scoped>
.steam-deck-sale .featured-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
}

.sale-card {
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.sale-card__image {
  display: block;
}

.sale-card__image img {
  width: 100%;
  height: 140px;
  object-fit: cover;
}

.sale-card__body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sale-card__blurb {
  color: var(--muted);
  line-height: 1.5;
  margin: 0;
}

.sale-card__meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
}

.sale-card__discount {
  color: #14e06e;
}

.sale-card__price {
  color: var(--text);
}

.sale-card__cta {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  background: rgba(102, 192, 244, 0.16);
  color: var(--primary);
  border-radius: 999px;
  text-decoration: none;
  font-weight: 600;
  transition: background 0.2s ease;
}

.sale-card__cta:hover {
  background: rgba(102, 192, 244, 0.3);
}

.sale-table {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sale-table__header {
  display: grid;
  grid-template-columns: 2fr 0.8fr 0.8fr 0.6fr;
  gap: 12px;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--muted);
}

.sale-table__row {
  display: grid;
  grid-template-columns: 2fr 0.8fr 0.8fr 0.6fr;
  gap: 12px;
  align-items: center;
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 14px;
  padding: 16px;
}

.sale-table__info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.sale-table__info img {
  width: 140px;
  height: 66px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid rgba(102, 192, 244, 0.2);
}

.sale-table__info h3 {
  font-size: 1.05rem;
  margin-bottom: 4px;
}

.sale-table__info p {
  color: var(--muted);
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0;
}

.sale-table__meta {
  display: contents;
}

.sale-table__discount {
  color: #14e06e;
  font-weight: 700;
  font-size: 1.1rem;
}

.sale-table__price {
  font-weight: 600;
}

.sale-table__link {
  justify-self: end;
  color: var(--primary);
  text-decoration: none;
  font-weight: 600;
}

.sale-table__link:hover {
  color: var(--primary-hover);
}

.cta-banner {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: linear-gradient(135deg, rgba(102, 192, 244, 0.18), rgba(20, 224, 110, 0.2));
  border: 1px solid rgba(102, 192, 244, 0.35);
  border-radius: 18px;
  padding: 28px;
  text-align: center;
}

.cta-banner__button {
  align-self: center;
  padding: 12px 24px;
  background: #14e06e;
  color: #031019;
  border-radius: 999px;
  font-weight: 700;
  text-decoration: none;
}

.cta-banner__button:hover {
  background: #3df488;
}

@media (max-width: 860px) {
  .sale-table__header,
  .sale-table__row {
    grid-template-columns: 1fr;
  }

  .sale-table__info img {
    width: 120px;
    height: 56px;
  }

  .sale-table__header {
    display: none;
  }

  .sale-table__row {
    gap: 16px;
    padding: 18px 16px;
  }

  .sale-table__meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px 14px;
    padding-top: 4px;
  }

  .sale-table__discount {
    font-size: 1.25rem;
  }

  .sale-table__price {
    font-size: 1.05rem;
    font-weight: 700;
  }

  .sale-table__link {
    justify-self: stretch;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 12px 0;
    border-radius: 12px;
    border: 1px solid rgba(102, 192, 244, 0.35);
    background: rgba(102, 192, 244, 0.18);
    color: var(--primary);
    font-weight: 700;
    text-align: center;
    transition: background 0.2s ease, border-color 0.2s ease;
    margin-top: 4px;
  }

  .sale-table__link:hover {
    background: rgba(102, 192, 244, 0.28);
    border-color: rgba(102, 192, 244, 0.5);
  }
}
</style>
